const LIVENESS_TICK_MS = 5_000;
const WORKLET_URL = '/live-voice-liveness-worklet.js';
const WORKER_URL = '/live-voice-liveness-worker.js';
const WORKLET_PROCESSOR = 'imai-live-voice-liveness-tick';

/**
 * Drive liveness beats from the audio render thread, with a worker timer as a
 * second source. Hidden tabs throttle main-thread timers to about once a
 * minute; neither of these clocks is throttled that way. Each source only
 * nudges the caller, which rate-limits the actual beat.
 */
export function startLiveVoiceLivenessClock(onTick: () => void): {
  dispose: () => void;
} {
  let disposed = false;
  let context: AudioContext | undefined;
  let node: AudioWorkletNode | undefined;
  let worker: Worker | undefined;
  const tick = () => {
    if (!disposed) onTick();
  };

  const startWorklet = async () => {
    if (
      typeof AudioContext === 'undefined' ||
      typeof AudioWorkletNode === 'undefined'
    )
      return;
    context = new AudioContext();
    await context.audioWorklet.addModule(WORKLET_URL);
    if (disposed) return;
    node = new AudioWorkletNode(context, WORKLET_PROCESSOR, {
      numberOfInputs: 0,
      outputChannelCount: [1],
      processorOptions: {
        intervalFrames: Math.round(
          (context.sampleRate * LIVENESS_TICK_MS) / 1000
        ),
      },
    });
    node.port.onmessage = tick;
    // The processor writes no samples, so this output is silence.
    node.connect(context.destination);
    await context.resume();
  };
  void startWorklet().catch(() => {
    // The worker clock still drives beats; release the unused audio context.
    node?.disconnect();
    if (context) void context.close().catch(() => undefined);
    node = undefined;
    context = undefined;
  });

  try {
    if (typeof Worker !== 'undefined') {
      worker = new Worker(WORKER_URL);
      worker.onmessage = tick;
      worker.postMessage(LIVENESS_TICK_MS);
    }
  } catch {
    // The audio clock still drives beats when workers are unavailable.
  }

  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      worker?.terminate();
      if (node) node.port.onmessage = null;
      node?.disconnect();
      if (context) void context.close().catch(() => undefined);
    },
  };
}
